import { useState } from 'react';
import {
  Container,
  Title,
  Group,
  Badge,
  Card,
  Table,
  Text,
  Loader,
  Alert,
  Pagination,
  SegmentedControl,
  ActionIcon,
  Tooltip,
  Modal,
  Textarea,
  Button,
  Stack,
} from '@mantine/core';
import {
  IconExclamationCircle,
  IconRefresh,
  IconCashBanknote,
  IconCheck,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useApi, useApiClient } from '../../api';
import { formatCents } from '../../components/products/productDisplay';

const PAGE_LIMIT = 20;

const STATUS_META = {
  requested: { color: 'yellow', label: 'Pending' },
  paid: { color: 'green', label: 'Paid' },
  rejected: { color: 'red', label: 'Rejected' },
};

// Admin payouts queue (contract §4.18): sellers request payouts, admins pay
// manually out-of-band and mark them paid/rejected here (PATCH sets
// resolved_at and notifies the seller with payout.resolved). No admin.*
// realtime topic exists for payouts, so this is a plain useApi with a manual
// refresh — unlike ReportsQueue's useLiveQuery.
const AdminPayoutsPage = () => {
  const api = useApiClient();
  // Default to the actionable work queue.
  const [statusFilter, setStatusFilter] = useState('requested');
  const [page, setPage] = useState(1);
  // { payout, status: 'paid' | 'rejected' } while the confirm modal is open
  const [pendingAction, setPendingAction] = useState(null);
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  const { data, loading, error, refetch } = useApi('/api/v1/admin/payouts', {
    page,
    limit: PAGE_LIMIT,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const payouts = data?.items ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.total ?? 0) / (data?.limit ?? PAGE_LIMIT)));

  const handleFilterChange = (value) => {
    setStatusFilter(value);
    setPage(1);
  };

  const openAction = (payout, status) => {
    setPendingAction({ payout, status });
    setAdminNote('');
  };

  const confirmAction = async () => {
    if (!pendingAction || saving) return;
    setSaving(true);
    try {
      const body = { status: pendingAction.status };
      const note = adminNote.trim();
      if (note) body.admin_note = note;
      await api.patch(`/api/v1/admin/payouts/${pendingAction.payout.id}`, body);
      notifications.show({
        title: 'Payout updated',
        message: `Payout #${pendingAction.payout.id} ${pendingAction.status}`,
        color: 'green',
        icon: <IconCheck size="1rem" />,
      });
      setPendingAction(null);
      refetch();
    } catch (err) {
      notifications.show({
        title: 'Update failed',
        message: err?.message || 'Could not update payout',
        color: 'red',
      });
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading && payouts.length === 0) {
    return (
      <Container size="xl" py="md">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Group align="center" mb="lg">
        <IconCashBanknote size="1.5rem" color="var(--mantine-color-green-6)" />
        <Title order={1} size="h2">Payouts</Title>
        <Badge color="pink" variant="light">Admin</Badge>
      </Group>

      <Card shadow="sm" padding="lg" radius="md" withBorder mb="md">
        <Group justify="space-between">
          <SegmentedControl
            value={statusFilter}
            onChange={handleFilterChange}
            data={[
              { value: 'requested', label: 'Requested' },
              { value: 'paid', label: 'Paid' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'all', label: 'All' },
            ]}
          />
          <ActionIcon onClick={refetch} variant="light" aria-label="Refresh">
            <IconRefresh size="1rem" />
          </ActionIcon>
        </Group>
        {error && (
          <Alert icon={<IconExclamationCircle size="1rem" />} color="red" mt="md">
            {error.message}
          </Alert>
        )}
      </Card>

      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Status</Table.Th>
              <Table.Th>Seller</Table.Th>
              <Table.Th>Method</Table.Th>
              <Table.Th>Amount</Table.Th>
              <Table.Th>Requested</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {payouts.map((payout) => {
              const meta = STATUS_META[payout.status] || { color: 'gray', label: payout.status };
              return (
                <Table.Tr key={payout.id}>
                  <Table.Td>
                    {payout.admin_note ? (
                      <Tooltip label={payout.admin_note} multiline maw={320} withArrow>
                        <Badge color={meta.color} variant="light">{meta.label}</Badge>
                      </Tooltip>
                    ) : (
                      <Badge color={meta.color} variant="light">{meta.label}</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={500}>{payout.seller?.display_name}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{payout.payout_method || '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" fw={600}>{formatCents(payout.amount_cents)}</Text>
                  </Table.Td>
                  <Table.Td>{formatDate(payout.requested_at)}</Table.Td>
                  <Table.Td>
                    {payout.status === 'requested' ? (
                      <Group gap="xs" wrap="nowrap">
                        <Button
                          size="compact-sm"
                          variant="light"
                          color="green"
                          onClick={() => openAction(payout, 'paid')}
                        >
                          Mark paid
                        </Button>
                        <Button
                          size="compact-sm"
                          variant="light"
                          color="red"
                          onClick={() => openAction(payout, 'rejected')}
                        >
                          Reject
                        </Button>
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">
                        {payout.resolved_at ? formatDate(payout.resolved_at) : '—'}
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>

        {payouts.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Text c="dimmed">No payout requests found</Text>
          </div>
        )}

        {totalPages > 1 && (
          <Group align="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Card>

      {/* Mark paid / Reject confirmation modal */}
      <Modal
        opened={!!pendingAction}
        onClose={() => setPendingAction(null)}
        title={
          pendingAction
            ? `${pendingAction.status === 'paid' ? 'Mark paid' : 'Reject'}: payout #${pendingAction.payout.id}`
            : ''
        }
        centered
        transitionProps={{ duration: 0 }}
      >
        <Stack gap="md">
          {pendingAction && (
            <Text size="sm">
              {formatCents(pendingAction.payout.amount_cents)} to{' '}
              <Text span fw={600}>{pendingAction.payout.seller?.display_name}</Text>
              {' '}via {pendingAction.payout.payout_method || '—'}
            </Text>
          )}
          <Textarea
            label="Admin note"
            placeholder="Optional note — visible to the seller"
            value={adminNote}
            onChange={(e) => setAdminNote(e.currentTarget.value)}
            maxLength={2000}
            autosize
            minRows={3}
          />
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              color="gray"
              onClick={() => setPendingAction(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color={pendingAction?.status === 'paid' ? 'green' : 'red'}
              onClick={confirmAction}
              loading={saving}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default AdminPayoutsPage;
